/**
 * Mar 3, 2026 · Inventory Executive Dashboard.
 * Feb 2026 · Period-toggle rhythm (Total / Today / Yesterday / Week /
 * MTD / Last Month) — mirrors Executive Sales Overview pattern.
 *
 * Period toggle scopes Inward / Outward / Opening-Closing to the picked
 * window. Operational tiles (RM Stock Value / Active Lots / Low-Stock /
 * Total Lots) stay point-in-time.
 *
 * Two roundtrips → `/api/dashboard/inventory-kpis` (unchanged) plus
 * `/api/dashboard/inventory-flow?period=<key>`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  PackageIcon, WarehouseIcon, WarningIcon, ArrowRightIcon,
  ArrowsCounterClockwiseIcon,
} from "@phosphor-icons/react";

const fmtInr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtQty = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtRate = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIODS = [
  { k: "total",     label: "Total View" },
  { k: "today",     label: "Today" },
  { k: "yesterday", label: "Yesterday" },
  { k: "week",      label: "This Week" },
  { k: "mtd",       label: "MTD" },
  { k: "lastmonth", label: "Last Month" },
];

const PERIOD_ACCENT = {
  total:     "text-emerald-300",
  today:     "text-[var(--accent)]",
  yesterday: "text-amber-300",
  week:      "text-cyan-300",
  mtd:       "text-indigo-300",
  lastmonth: "text-fuchsia-300",
};

function Tile({ label, value, sub, color = "text-white", tid, to }) {
  const Wrap = to ? Link : "div";
  const props = to ? { to } : {};
  return (
    <Wrap {...props} data-testid={tid} className={`tactical-card p-4 flex flex-col justify-between min-h-[110px] ${to ? "hover:border-[var(--accent)] transition" : ""}`}>
      <div className="overline text-[10px] text-zinc-400">{label}</div>
      <div className={`font-mono text-2xl sm:text-3xl font-black tabular-nums leading-tight ${color}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{sub}</div>}
    </Wrap>
  );
}

function MiniTile({ label, value, sub, icon: Icon, tone = "text-white", testId, to }) {
  const Wrap = to ? Link : "div";
  const props = to ? { to } : {};
  return (
    <Wrap {...props} data-testid={testId} className={`tactical-card p-3 flex flex-col gap-1.5 ${to ? "hover:border-[var(--accent)] transition" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="overline text-zinc-400">{label}</div>
        {Icon && <Icon size={14} weight="bold" className="text-zinc-500" />}
      </div>
      <div className={`font-mono text-xl sm:text-2xl font-black tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{sub}</div>}
    </Wrap>
  );
}

export default function InventoryDashboard() {
  const [d, setD] = useState(null);
  const [flow, setFlow] = useState(null);
  const [period, setPeriod] = useState("total");

  const load = async (p = period) => {
    try {
      const [k, f] = await Promise.all([
        api.get("/dashboard/inventory-kpis"),
        api.get(`/dashboard/inventory-flow?period=${p}`),
      ]);
      setD(k.data);
      setFlow(f.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => {
    load(period);
    const t = setInterval(() => load(period), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (!d) return <div className="p-8 text-zinc-400" data-testid="inventory-dashboard-loading">Loading inventory snapshot…</div>;

  const rcv = flow?.received || {};
  const iss = flow?.issued || {};
  const opening = flow?.opening_balance || {};
  const closing = flow?.closing_balance || {};
  const consumedPct = rcv.qty > 0 ? Math.round((iss.qty / rcv.qty) * 100) : 0;
  const accent = PERIOD_ACCENT[period] || "text-emerald-300";
  const periodLabel = flow?.period_label || "Total";

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="inventory-dashboard">
      {/* Header · centered executive layout — mirrors Sales Dashboard */}
      <div className="text-center pt-2 pb-6 border-b border-[var(--border)]">
        <div className="overline text-[10px] tracking-[0.3em] text-zinc-500">NARROW FABRIC · REAL-TIME KPIs</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-white mt-2">Inventory Overview</h1>
        <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
          <div className="inline-flex rounded-md border border-[var(--border)] overflow-hidden text-xs font-mono" data-testid="inventory-view-toggle">
            {PERIODS.map((b, i) => (
              <button
                key={b.k}
                onClick={() => setPeriod(b.k)}
                data-testid={`inventory-view-${b.k}`}
                className={`h-10 sm:h-9 px-3 uppercase tracking-widest ${i > 0 ? "border-l border-[var(--border)]" : ""} ${period === b.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
              >{b.label}</button>
            ))}
          </div>
          <button onClick={() => load(period)} className="h-10 sm:h-9 px-3 rounded-md border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs flex items-center gap-1.5" data-testid="inventory-dashboard-refresh">
            <ArrowsCounterClockwiseIcon size={12} /> Refresh
          </button>
          <Link to="/inventory" className="text-[11px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1" data-testid="inventory-open-ledger-link">
            Open Ledger<ArrowRightIcon size={11} weight="bold" />
          </Link>
        </div>
      </div>

      {/* Row 1 · Inward */}
      <div>
        <div className={`overline mb-2 ${accent}`}>Inward · {periodLabel}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Tile label="Inward" value={fmtQty(rcv.qty)}       sub="Qty (units · kg)"    color="text-white"       tid="inv-rcv-qty" />
          <Tile label="Inward" value={fmtInr(rcv.value)}     sub="Value"               color={accent}           tid="inv-rcv-value" />
          <Tile label="Inward" value={fmtRate(rcv.avg_price)} sub="Avg Price / unit"   color={accent}           tid="inv-rcv-avg" />
        </div>
      </div>

      {/* Row 2 · Outward */}
      <div>
        <div className="overline mb-2 text-amber-300">Outward · {periodLabel} {consumedPct > 0 ? <span className="ml-2 text-[10px] font-mono text-zinc-500">({consumedPct}% of inward consumed)</span> : null}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Tile label="Outward" value={fmtQty(iss.qty)}       sub="Qty (units · kg)"    color="text-white"    tid="inv-iss-qty" />
          <Tile label="Outward" value={fmtInr(iss.value)}     sub="Value"               color="text-amber-300" tid="inv-iss-value" />
          <Tile label="Outward" value={fmtRate(iss.avg_price)} sub="Avg Price / unit"   color="text-amber-300" tid="inv-iss-avg" />
        </div>
      </div>

      {/* Row 3 · Opening / Closing Balance */}
      <div>
        <div className="overline mb-2 text-sky-300">Opening / Closing Balance · {periodLabel}</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Tile label="Opening Qty" value={fmtQty(opening.qty)}     sub="Qty at window start"   color="text-white" tid="inv-open-qty" />
          <Tile label="Opening Value" value={fmtInr(opening.value)} sub="Value at window start" color="text-sky-300" tid="inv-open-value" />
          <Tile label="Closing Qty" value={fmtQty(closing.qty)}     sub="Qty at window end"     color="text-white" tid="inv-close-qty" />
          <Tile label="Closing Value" value={fmtInr(closing.value)} sub="Value at window end"   color="text-sky-300" tid="inv-close-value" />
        </div>
      </div>

      {/* Operational KPIs (point-in-time) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniTile label="RM Stock Value" value={fmtInr(d.stock_value)} sub={`${d.materials_count} materials`} icon={WarehouseIcon} testId="tile-stock-value" />
        <MiniTile label="Active Lots" value={d.active_lots} sub={`${fmtQty(d.active_lot_qty)} kg total`} icon={PackageIcon} tone="text-[var(--accent)]" testId="tile-active-lots" />
        <MiniTile label="Low-Stock Alerts" value={d.low_stock_count} icon={WarningIcon}
          tone={d.low_stock_count > 0 ? "text-red-400" : "text-emerald-400"}
          sub={d.low_stock_count > 0 ? "below reorder threshold" : "all healthy"}
          testId="tile-low-stock" />
        <MiniTile label="Total Lots" value={d.lots_count} icon={PackageIcon} testId="tile-total-lots" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="tactical-card p-4">
          <div className="overline mb-3">Stock by Category</div>
          {(d.by_category || []).length === 0 ? <div className="text-xs text-zinc-500 py-6 text-center">No stock recorded</div> :
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left py-2">Category</th><th className="text-right">Materials</th><th className="text-right">Stock (kg)</th>
              </tr></thead>
              <tbody>
                {d.by_category.map(c => (
                  <tr
                    key={c.category}
                    className="border-b border-[var(--border)]/40 hover:bg-[var(--accent)]/5 cursor-pointer transition-colors"
                    data-testid={`inv-cat-${c.category}`}
                    onClick={() => { window.location.href = `/stock-register?category=${encodeURIComponent(c.category)}`; }}
                    title={`Drill into Stock Register filtered to ${c.category}`}
                  >
                    <td className="py-2 text-white group-hover:text-[var(--accent)]">
                      <span className="underline decoration-dotted decoration-zinc-600 underline-offset-4 hover:decoration-[var(--accent)]">{c.category}</span>
                      <ArrowRightIcon size={10} weight="bold" className="inline ml-2 text-zinc-500" />
                    </td>
                    <td className="text-right font-mono">{c.count}</td>
                    <td className="text-right font-mono text-[var(--accent)]">{fmtQty(c.stock)}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </div>
        <div className="tactical-card p-4">
          <div className="overline mb-3 text-red-300">Low-Stock Items</div>
          {(d.low_stock_items || []).length === 0 ? <div className="text-xs text-zinc-500 py-6 text-center">All materials above their reorder threshold.</div> :
            <ul className="space-y-2">
              {d.low_stock_items.map(m => (
                <li key={m.id || m.rm_code} className="flex items-center justify-between border border-red-500/30 bg-red-500/5 rounded-sm px-3 py-2" data-testid={`low-stock-${m.rm_code || m.id}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-white truncate">{m.rm_code || m.code || "—"}</div>
                    <div className="text-[11px] text-zinc-400 truncate">{m.name || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-red-300">{fmtQty(m.current_stock)} {m.uom || ""}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">min {fmtQty(m.min_stock)}</div>
                  </div>
                </li>
              ))}
            </ul>}
        </div>
      </div>
    </div>
  );
}
